import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { Check, ChevronDownIcon, X } from 'lucide-react'
import * as React from 'react'

export type SearchableSelectOption = {
  value: string
  label: string
  description?: string
  searchValue?: string
  disabled?: boolean
}

export type SearchableSelectGroup = {
  label?: string
  options: SearchableSelectOption[]
}

type SearchableSelectProps = {
  value: string
  onValueChange: (value: string) => void
  options?: SearchableSelectOption[]
  groups?: SearchableSelectGroup[]
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  triggerClassName?: string
  testId?: string
}

export function SearchableSelect({
  value,
  onValueChange,
  options,
  groups,
  placeholder = 'Select option',
  searchPlaceholder = 'Search...',
  emptyText = 'No options found',
  disabled = false,
  className,
  triggerClassName,
  testId,
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false)
  const optionGroups = groups ?? [{ options: options ?? [] }]
  const flatOptions = optionGroups.flatMap(group => group.options)
  const selectedOption = flatOptions.find(option => option.value === value)
  const selectedCmdkValue = selectedOption
    ? (selectedOption.searchValue ?? `${selectedOption.label} ${selectedOption.description ?? ''}`)
    : ''

  function handleSelect(optionValue: string) {
    onValueChange(optionValue)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid={testId}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('h-9 w-full justify-between bg-secondary/50 px-3 text-xs font-normal', triggerClassName)}
        >
          <span className={cn('truncate', !selectedOption && 'text-muted-foreground')}>
            {selectedOption?.label ?? placeholder}
          </span>
          <div className="ml-auto flex items-center gap-1">
            {value ? (
              <span
                role="button"
                className="flex size-5 items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
                onClick={e => {
                  e.stopPropagation()
                  onValueChange('')
                }}
              >
                <X className="size-3" />
              </span>
            ) : null}
            <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        usePortal={false}
        className={cn('w-[var(--radix-popover-trigger-width)] p-0', className)}
        align="start"
      >
        <Command value={selectedCmdkValue}>
          <CommandInput placeholder={searchPlaceholder} className="text-xs" />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {optionGroups.map((group, groupIndex) =>
              group.options.length > 0 ? (
                <CommandGroup key={group.label ?? groupIndex} heading={group.label}>
                  {group.options.map(option => (
                    <CommandItem
                      key={option.value}
                      data-testid={`${testId}-option-${option.value}`}
                      value={option.searchValue ?? `${option.label} ${option.description ?? ''}`}
                      onSelect={() => {
                        if (!option.disabled) {
                          handleSelect(option.value)
                        }
                      }}
                      className={cn('items-start text-xs', option.disabled && 'cursor-not-allowed opacity-50')}
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate font-medium text-foreground">{option.label}</span>
                        {option.description ? (
                          <span className="truncate text-[11px] text-muted-foreground">{option.description}</span>
                        ) : null}
                      </span>
                      {value === option.value ? <Check className="ml-auto size-4 text-primary" /> : null}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null,
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
